import type { ToastType } from '../toaster/redux/actions';
import { Options, Response } from '../anaxios';
import { reportHttpError } from './http-error-reporting';
import { FormattedMessage } from 'react-intl';

import { Link as ReachLink } from 'react-router';
import { Link } from '../typography';

export const CODE_TO_MESSAGE: Record<
  string,
  {
    report?: boolean;
    message?: [
      text: string | React.ReactNode,
      type: ToastType,
      timeout?: number,
    ];
  }
> = {
  0: {
    report: false,
    message: ['Network error.', 'alert'],
  },
  408: {
    report: true,
    message: ['Request timed out.', 'alert'],
  },
  409: {
    report: true,
    message: ['Conflict detected.', 'warning'],
  },
  401: {
    report: true,
    message: ['Session expired.', 'warning'],
  },
  403: {
    report: true,
    message: ['Permission denied.', 'alert'],
  },
  413: {
    report: true,
    message: ['File too large.', 'alert'],
  },
  402: {
    report: false,
    message: [
      <>
        You have run out of credits.{' '}
        <Link
          variant="underlinedLink"
          as={ReachLink}
          to="/app/settings/account/top-up"
        >
          Please top up.
        </Link>
      </>,
      'warning',
    ],
  },
};

function isUnavailableReference(status: number, data: unknown): boolean {
  if (status !== 409) return false;
  let response = data;
  if (typeof response === 'string') {
    try {
      response = JSON.parse(response);
    } catch {
      return false;
    }
  }
  const detail =
    response && typeof response === 'object' && 'detail' in response
      ? response.detail
      : undefined;
  return Boolean(
    detail &&
    typeof detail === 'object' &&
    'code' in detail &&
    detail.code === 'reference_unavailable',
  );
}

export const getErrorInterceptor = (
  logout = () => null,
  showToast = (
    message: [
      text: string | React.ReactNode,
      type: ToastType,
      timeout?: number,
    ],
  ) => null,
  codeToAction: Record<
    string,
    {
      report?: boolean;
      message?: [
        text: string | React.ReactNode,
        type: ToastType,
        timeout?: number,
      ];
    }
  > = CODE_TO_MESSAGE,
) => {
  return (
    error: Error & {
      config: Options;
      response: Response<any>;
      request: Request;
    },
  ) => {
    if (!error.response) {
      if (!(error instanceof Error)) {
        console.error("We're passing on non error rejections", error);
      }
      return Promise.reject(error);
    }

    const status = error?.response?.status;

    if (status === 401 && error.request.url.includes(location.host)) {
      logout();
    }

    const action: (typeof CODE_TO_MESSAGE)[string] = isUnavailableReference(
      status,
      error.response.data,
    )
      ? {
          report: true,
          message: [
            <FormattedMessage
              key="reference-unavailable"
              defaultMessage="A linked record is unavailable. Please wait a moment and try again."
              description="Retry guidance when a referenced record is not yet available"
            />,
            'warning',
          ],
        }
      : codeToAction[status];

    const report = () => reportHttpError(error);

    if (action) {
      if (action.message) {
        showToast(action.message);
      }

      if (action.report) {
        report();
      }
    } else {
      if (status == 429) {
        showToast(['Too many requests. Please try again later.', 'alert']);
        report();
      } else if (status >= 400 && status < 500) {
        showToast([
          'Whoops, it seems like we have hit a snag. Our team has been notified.',
          'alert',
        ]);
        report();
      } else if (status >= 500) {
        showToast([
          'Whoops, it seems like we have hit a snag. Our team has been notified.',
          'alert',
        ]);
        if (status > 500) {
          report();
        }
      }
    }

    if (!(error instanceof Error)) {
      console.error("We're passing on non error rejections", error);
    }

    return Promise.reject(error);
  };
};
